/**
 * Pure canvas geometry — no React, no DOM. Marquee and lasso hit-testing,
 * bounds, and the box-edge clip that connection arrows need.
 *
 * Kept pure because these are the parts worth being sure about: a lasso that
 * misses an object the user visibly enclosed is the difference between a
 * trustworthy selection gesture and a toy.
 */
/** Normalise a drag (which can run in any direction) into a positive box. */
export function rectFromDrag(a, b) {
    return {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        w: Math.abs(b.x - a.x),
        h: Math.abs(b.y - a.y),
    };
}
export function boxesIntersect(a, b) {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
export function boxContains(outer, inner) {
    return inner.x >= outer.x && inner.y >= outer.y
        && inner.x + inner.w <= outer.x + outer.w
        && inner.y + inner.h <= outer.y + outer.h;
}
export function centerOf(b) {
    return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
}
/**
 * Ray-casting point-in-polygon. Counts crossings of a ray cast to +x; a point
 * exactly on an edge may land either way, which is fine for a hand-drawn lasso.
 */
export function pointInPolygon(p, poly) {
    if (poly.length < 3)
        return false;
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const yi = poly[i].y;
        const yj = poly[j].y;
        // Only edges that straddle the ray's y can cross it.
        if ((yi > p.y) === (yj > p.y))
            continue;
        const xAt = ((poly[j].x - poly[i].x) * (p.y - yi)) / (yj - yi) + poly[i].x;
        if (p.x < xAt)
            inside = !inside;
    }
    return inside;
}
/**
 * Which objects a lasso caught. An object counts when its CENTRE is inside the
 * loop: enclosing every corner is far too strict for a quick scribble, and
 * "any corner" grabs neighbours you deliberately drew around.
 */
export function lassoHits(poly, objects) {
    if (poly.length < 3)
        return [];
    return objects.filter((o) => pointInPolygon(centerOf(o), poly)).map((o) => o.id);
}
/**
 * Which objects a marquee caught. Touch-to-select (any overlap), which is what
 * a whiteboard wants — Figma's default too. Pass `contain` for the stricter
 * fully-enclosed rule.
 */
export function marqueeHits(rect, objects, opts = {}) {
    const test = opts.contain ? (o) => boxContains(rect, o) : (o) => boxesIntersect(rect, o);
    return objects.filter((o) => test(o)).map((o) => o.id);
}
/** Bounding box of a set of boxes; null when empty. */
export function boundsOf(objects) {
    if (!objects.length)
        return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const o of objects) {
        if (o.x < minX)
            minX = o.x;
        if (o.y < minY)
            minY = o.y;
        if (o.x + o.w > maxX)
            maxX = o.x + o.w;
        if (o.y + o.h > maxY)
            maxY = o.y + o.h;
    }
    return { minX, minY, maxX, maxY };
}
/**
 * Where a segment aimed at a box's centre crosses that box's edge (+pad), so
 * connector arrowheads land ON the border instead of buried under the card.
 */
export function clipToBox(from, box, pad = 8) {
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const hw = box.w / 2 + pad;
    const hh = box.h / 2 + pad;
    const dx = cx - from.x;
    const dy = cy - from.y;
    if (!dx && !dy)
        return { x: cx, y: cy };
    const tx = dx ? hw / Math.abs(dx) : Infinity;
    const ty = dy ? hh / Math.abs(dy) : Infinity;
    const t = Math.max(0, 1 - Math.min(tx, ty));
    return { x: from.x + dx * t, y: from.y + dy * t };
}
/**
 * Drop points that are within `min` of the previous one. A pointermove stream
 * emits far more samples than a lasso needs, and every one costs a
 * point-in-polygon crossing test per object per frame.
 */
export function simplifyPath(points, min = 4) {
    if (points.length < 3)
        return [...points];
    const out = [points[0]];
    for (let i = 1; i < points.length; i++) {
        const last = out[out.length - 1];
        if (Math.hypot(points[i].x - last.x, points[i].y - last.y) >= min)
            out.push(points[i]);
    }
    const tail = points[points.length - 1];
    if (out[out.length - 1] !== tail)
        out.push(tail);
    return out;
}
/** An SVG path for a freehand loop, closed back to the start. */
/**
 * An SVG `d` attribute from a list of points.
 *
 * `close` is explicit and has no default on purpose. A LASSO is a closed
 * loop — it has to be, or the region it encloses is undefined — while an
 * INK stroke must stay open, because closing it draws a line from where
 * the pen lifted back to where it started. The two callers want opposite
 * answers and neither is the obvious one to default to.
 */
export function pathFromPoints(points, close) {
    if (!points.length)
        return '';
    const d = points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join('');
    return close ? `${d}Z` : d;
}
